import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * GÖRGETÉSKEZELÉS KÉPERNYŐVÁLTÁSKOR.
 *
 * Két dolgot csinál, és a kettő összetartozik:
 *
 *   ELŐRE (`PUSH`/`REPLACE`) → a lap tetejére ugrik. ⚠️ Ez egy MÉRT HIBA
 *   javítása: a feedet 2600 képpontig görgetve és onnan egy profilt
 *   megnyitva a `scrollY` 2600 MARADT, tehát a profil közepén landolt a
 *   felhasználó. Egylapos alkalmazásban a böngésző nem nullázza a
 *   görgetést, mert nem tölt új dokumentumot.
 *
 *   VISSZA (`POP`) → visszaállítja, ahol az adott előzmény-bejegyzésen
 *   tartottunk. Geri kérése (2026-09-16): a feedből egy aktivitást vagy
 *   profilt megnyitva és visszalépve ott folytatódjon a lista, ahol
 *   abbahagytuk — Androidon a natív vissza gombbal is, ami ugyanezt a
 *   `POP`-ot váltja ki (`useAndroidBack` → `navigate(-1)`).
 *
 * ⚠️ AZ ELŐZMÉNY-BEJEGYZÉS KULCSÁRA (`location.key`) MENTÜNK, nem az
 * útvonalra. Ha valaki kétszer nyitja meg ugyanazt a feedet két különböző
 * mélységben, a két bejegyzés külön pozíciót tart — útvonal szerint
 * kulcsolva a második felülírná az elsőt.
 *
 * ⚠️ A FEED ADATA MÁR MEGMARAD (`useActivities` → `useInfiniteQuery`, a
 * betöltött lapokkal együtt gyorsítótárazva), ezért a visszatérő lista
 * ugyanolyan hosszú lesz. A magasság viszont nem azonnal áll össze: a
 * térképek és az avatárok később töltenek be. Ezért a visszaállítás nem
 * egyszeri, hanem addig próbálkozik, amíg a lap tényleg elég magas lesz.
 */

/**
 * Előzmény-kulcs → görgetési pozíció.
 *
 * MODUL-SZINTŰ, szándékosan nem `sessionStorage`: újratöltés után a feed
 * gyorsítótára is üres, tehát egy megőrzött pozíció úgyis elérhetetlen
 * lenne — és rosszabb: a lista tetejére dobná a felhasználót egy fél
 * másodperces ugrálás után.
 */
const positions = new Map<string, number>();

/** Legfeljebb ennyi ideig próbálkozunk, amíg a lap elég magas lesz. */
const RESTORE_WINDOW_MS = 1200;

/** Ilyen sűrűn ismételjük meg a beállítást a türelmi időn belül. */
const RETRY_EVERY_MS = 50;

export function ScrollManager() {
  const { key } = useLocation();
  const navigationType = useNavigationType();
  const currentKey = useRef(key);

  /*
    A BÖNGÉSZŐ SAJÁT VISSZAÁLLÍTÁSA KIKAPCSOLVA. Egylapos alkalmazásban
    úgyis elhibázza (a visszalépés pillanatában a lap még rövid), viszont
    közben versenyt futna ezzel a kóddal — a kettő együtt ugráló képet ad.
  */
  useEffect(() => {
    const previous = history.scrollRestoration;
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    return () => {
      if ('scrollRestoration' in history) history.scrollRestoration = previous;
    };
  }, []);

  /*
    FOLYAMATOSAN MENTÜNK, nem csak képernyőváltáskor.

    A `location.key` már az ÚJ bejegyzésé, mire a váltás effektje lefut —
    a régi pozíciót tehát nem lehetne onnan elmenteni.

    ⚠️ SZINKRON ÍRÁS, nem `requestAnimationFrame`-mel késleltetve. Egy
    képkockával elhalasztott mentés lemaradhat az utolsó görgetésről, ha a
    felhasználó görgetés után azonnal koppint egy kártyára. Egy `Map.set`
    így is jelentéktelen munka.
  */
  useEffect(() => {
    function remember() {
      positions.set(currentKey.current, window.scrollY);
    }
    /*
      ⚠️ KOPPINTÁSKOR IS RÖGZÍTÜNK, nem csak görgetéskor — MÉRT HIBA
      javítása. A `scroll` esemény nem azonnal érkezik: mérve ~600 ms is
      eltelt a `scrollTo` és a kezelő lefutása között. Aki görgetés után
      rögtön rákoppint egy kártyára, annál tehát a navigáció ELŐBB történt
      volna meg, mint a mentés — és a később beérkező esemény már a friss
      kulcsra írt volna. Hat próbából három emiatt landolt rossz helyen.

      A `pointerdown` a felfogó fázisban fut, tehát még azelőtt, hogy a
      React kattintás-kezelője navigálna: ilyenkor a pozíció és a kulcs is
      biztosan a RÉGI.
    */
    window.addEventListener('scroll', remember, { passive: true });
    window.addEventListener('pointerdown', remember, { capture: true, passive: true });
    window.addEventListener('keydown', remember, { capture: true });
    return () => {
      window.removeEventListener('scroll', remember);
      window.removeEventListener('pointerdown', remember, { capture: true });
      window.removeEventListener('keydown', remember, { capture: true });
    };
  }, []);

  /**
   * ⚠️ `useLayoutEffect`, ÉS EZ EGY MÉRT HIBA JAVÍTÁSA.
   *
   * Képernyőváltáskor a böngésző a DOM cseréje után azonnal LEVÁGJA a
   * görgetést az új, rövidebb lap magasságára, és kerít hozzá egy `scroll`
   * eseményt. Ha a kulcsot csak passzív effektben (`useEffect`) frissítenénk,
   * versenyhelyzet lenne: a levágás eseménye még a RÉGI kulcsra írhatna,
   * tehát nulázná a feed elmentett helyét. Mérve: az aktivitás-részletezőből
   * visszalépve 2600 helyett 0 jött vissza.
   *
   * A layout-effekt a kirajzolás ELŐTT fut, tehát mielőtt bármelyik
   * `scroll` esemény kézbesülne — a levágás így már az új kulcsra ír, ahol
   * nem okoz kárt. Ráadásul a visszaállítás is kirajzolás előtt történik,
   * tehát nem villan fel a lap teteje.
   */
  useLayoutEffect(() => {
    currentKey.current = key;

    if (navigationType !== 'POP') {
      positions.set(key, 0);
      window.scrollTo(0, 0);
      return;
    }

    const target = positions.get(key) ?? 0;
    if (target <= 0) {
      window.scrollTo(0, 0);
      return;
    }

    /*
      A FELHASZNÁLÓ NYER: ha közben hozzáér a laphoz, abbahagyjuk. Egy
      makacs visszaállítás elrángatná az ujja alól a tartalmat.
    */
    /*
      A FELHASZNÁLÓ NYER: ha közben hozzáér a laphoz, abbahagyjuk. Egy
      makacs visszaállítás elrángatná az ujja alól a tartalmat.
    */
    let tick = 0;
    const apply = () => {
      const furthest = document.documentElement.scrollHeight - window.innerHeight;
      window.scrollTo(0, Math.min(target, Math.max(0, furthest)));
    };
    const finish = () => {
      window.clearInterval(tick);
    };

    window.addEventListener('wheel', finish, { passive: true, once: true });
    window.addEventListener('touchstart', finish, { passive: true, once: true });
    window.addEventListener('keydown', finish, { once: true });
    /* Görgetősávval húzva sem `wheel`, sem `touchstart` nem jön — a
       `pointerdown` viszont igen. */
    window.addEventListener('pointerdown', finish, { passive: true, once: true });

    /*
      AZONNAL, MÉG KIRAJZOLÁS ELŐTT — a feed adata a gyorsítótárból jön,
      tehát a lap többnyire már itt elég magas. Így a felhasználó nem látja
      egy pillanatra sem a lista tetejét.
    */
    apply();

    /*
      ⚠️ IDŐZÍTŐ, NEM `requestAnimationFrame` — MÉRT HIBA javítása. Az rAF
      fojtható (háttérbe kerülő lap, energiatakarékos mód, beágyazott
      nézet): mérve a képkockák olyan ritkán jöttek, hogy a visszaállítás
      hol túl későn futott le, hol a türelmi időn túl is tovább rángatta a
      lapot — hat próbából hat rossz helyre érkezett. Az időzítő akkor is
      jár, amikor az rAF áll.

      Az ismétlés a későn betöltődő térképek és avatárok miatt kell: azok
      megváltoztatják a lap magasságát, és egyszeri beállítás után nem lenne,
      ami visszahozza a helyes pozíciót.
    */
    tick = window.setInterval(apply, RETRY_EVERY_MS);
    const stop = window.setTimeout(finish, RESTORE_WINDOW_MS);

    return () => {
      finish();
      window.clearTimeout(stop);
      window.removeEventListener('wheel', finish);
      window.removeEventListener('touchstart', finish);
      window.removeEventListener('keydown', finish);
      window.removeEventListener('pointerdown', finish);
    };
  }, [key, navigationType]);

  return null;
}
